import { useEffect, useMemo, useRef, useState } from "react";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

const isSafeAppPath = (value) =>
  typeof value === "string" && value.startsWith("/") && !value.startsWith("//");

const formatCreatedDate = (bookmark) => {
  const value = bookmark?.created_at || bookmark?.date;
  if (!value) return "Date unavailable";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(parsed);
};

const BookmarkGlyph = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path d="M5.5 2.75A1.75 1.75 0 017.25 1h5.5a1.75 1.75 0 011.75 1.75v14.13a.75.75 0 01-1.14.64L10 15.44l-3.36 2.08a.75.75 0 01-1.14-.64V2.75z" />
  </svg>
);

const YourBookmarks = ({ open, onClose }) => {
  const [bookmarks, setBookmarks] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    window.requestAnimationFrame(() => closeButtonRef.current?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;

    const token = localStorage.getItem("token");
    if (!token) {
      setBookmarks([]);
      setError("Sign in to see your bookmarks.");
      setStatus("error");
      return undefined;
    }

    const controller = new AbortController();

    const loadBookmarks = async () => {
      setStatus("loading");
      setError("");

      try {
        const response = await fetch(
          `${process.env.REACT_APP_USER_API_URL}/analysis/bookmarks`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );

        if (response.status === 401 || response.status === 403) {
          throw new Error("Your session has expired. Sign in again to view bookmarks.");
        }

        if (!response.ok) {
          throw new Error(`Could not load bookmarks (${response.status}).`);
        }

        const json = await response.json();
        const nextBookmarks = Array.isArray(json?.bookmarks)
          ? json.bookmarks.filter((item) => isSafeAppPath(item?.path))
          : [];

        setBookmarks(nextBookmarks);
        setStatus("ready");
      } catch (loadError) {
        if (loadError?.name === "AbortError") return;
        console.error("YourBookmarks load failed:", loadError);
        setBookmarks([]);
        setError(loadError?.message || "Could not load your bookmarks.");
        setStatus("error");
      }
    };

    loadBookmarks();
    return () => controller.abort();
  }, [open]);

  const countLabel = useMemo(() => {
    if (status === "loading") return "Loading saved places";
    if (bookmarks.length === 1) return "1 saved title";
    return `${bookmarks.length} saved titles`;
  }, [bookmarks.length, status]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90]" role="presentation">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-slate-950/45 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close your bookmarks"
      />

      <div className="absolute inset-x-3 bottom-3 top-3 flex items-center justify-center sm:inset-x-6 sm:bottom-6 sm:top-6">
        <section
          className="relative flex max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-[30px] border border-white/70 bg-white shadow-2xl shadow-slate-950/25"
          role="dialog"
          aria-modal="true"
          aria-labelledby="your-bookmarks-title"
          aria-describedby="your-bookmarks-summary"
        >
          <header className="border-b border-slate-200 bg-gradient-to-r from-white via-amber-50/60 to-white px-5 py-5 sm:px-7">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-amber-200 bg-amber-100 text-amber-700 shadow-sm">
                    <BookmarkGlyph />
                  </span>

                  <div className="min-w-0">
                    <h2
                      id="your-bookmarks-title"
                      className="truncate text-2xl font-black tracking-tight text-slate-950"
                    >
                      Your bookmarks
                    </h2>
                    <p
                      id="your-bookmarks-summary"
                      className="mt-0.5 text-sm font-semibold text-slate-500"
                    >
                      {countLabel} 
                    </p>
                  </div>
                </div>
              </div>

              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                aria-label="Close your bookmarks"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M5 5l10 10M15 5L5 15" />
                </svg>
              </button>
            </div>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/70 p-3 sm:p-5">
            {status === "loading" && (
              <div className="space-y-3" aria-live="polite">
                {[0, 1, 2].map((item) => (
                  <div
                    key={item}
                    className="animate-pulse rounded-[22px] border border-slate-200 bg-white p-4"
                  >
                    <div className="h-5 w-1/2 rounded bg-slate-200" />
                    <div className="mt-3 h-4 w-1/3 rounded bg-slate-100" />
                    <div className="mt-4 h-3 w-24 rounded bg-slate-100" />
                  </div>
                ))}
              </div>
            )}

            {status === "error" && (
              <div className="rounded-[24px] border border-red-200 bg-red-50 px-5 py-6 text-center">
                <div className="text-base font-black text-red-800">Bookmarks unavailable</div>
                <p className="mt-2 text-sm font-semibold leading-6 text-red-700/80">{error}</p>
              </div>
            )}

            {status === "ready" && bookmarks.length === 0 && (
              <div className="rounded-[26px] border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                  <BookmarkGlyph className="h-7 w-7" />
                </span>
                <div className="mt-4 text-lg font-black text-slate-900">No bookmarks yet</div>
                <p className="mx-auto mt-2 max-w-md text-sm font-semibold leading-6 text-slate-500">
                  Record a sentence in an audiobook or course. Its latest practice place will appear here.
                </p>
              </div>
            )}

            {status === "ready" && bookmarks.length > 0 && (
              <div className="space-y-2.5">
                {bookmarks.map((bookmark, index) => {
                  const partIndex = Number(bookmark?.share?.part_index);
                  const sentenceIndex = Number(bookmark?.share?.sentence_index);
                  const hasLocation =
                    Number.isFinite(partIndex) && Number.isFinite(sentenceIndex);

                  return (
                    <a
                      key={`${bookmark.title}-${bookmark.author || ""}-${bookmark.path}-${index}`}
                      href={bookmark.path}
                      onClick={onClose}
                      className={cx(
                        "group flex min-w-0 items-center gap-4 rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm",
                        "transition duration-200 hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-md",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      )}
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-amber-200 bg-amber-50 text-amber-600 transition group-hover:bg-amber-100 group-hover:text-amber-700">
                        <BookmarkGlyph />
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-base font-black text-slate-950">
                          {bookmark.title || "Untitled"}
                        </span>
                        <span className="mt-1 block truncate text-sm font-semibold text-slate-500">
                          {[bookmark.author, bookmark.language].filter(Boolean).join(" · ") || "Audiobook"}
                        </span>
                        {hasLocation && (
                          <span className="mt-2 inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-black text-slate-600">
                            Part {partIndex + 1} · Sentence {sentenceIndex + 1}
                          </span>
                        )}
                      </span>

                      <span className="shrink-0 text-right">
                        <span className="block text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                          Created
                        </span>
                        <span className="mt-1 block whitespace-nowrap text-xs font-bold text-slate-600">
                          {formatCreatedDate(bookmark)}
                        </span>
                        <svg className="ml-auto mt-3 h-4 w-4 text-slate-300 transition group-hover:translate-x-1 group-hover:text-amber-600" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M7 4l6 6-6 6" />
                        </svg>
                      </span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default YourBookmarks;
